import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { supabase } from '../../lib/supabase';
import { getStudentType, calculateTimelineMessage, getAllDeadlines } from '../../utils/collegeTimelineCalculator';
import { useFrameworkAutosave, SaveStatusIndicator } from '../../hooks/useFrameworkAutosave.jsx';
import { 
  ArrowLeft, 
  ArrowRight, 
  Copy, 
  CheckCircle, 
  Brain, 
  Lightbulb,
  Save,
  RefreshCw,
  User,
  Users,
  Sparkles,
  Target,
  FileText,
  AlertCircle,
  Settings,
  Zap,
  Compass,
  TrendingUp
} from 'lucide-react';

const SynthesisInstructionsGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, familyUsers } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  // Student selection logic (updated for auth_user_id approach)
  const getStudent = () => {
    const savedStudentId = localStorage.getItem('college-selected-student-id');
    
    if (savedStudentId && familyUsers?.length > 0) {
      // savedStudentId is now the auth_user_id, so match against that
      const savedStudent = familyUsers.find(u => 
        (u.authUserId || u.auth_user_id) === savedStudentId
      );
      if (savedStudent) return savedStudent;
    }
    
    if (familyUsers?.length > 0) {
      return familyUsers.find(user => user.type !== 'parent');
    }
    
    return null;
  };
  
  const student = getStudent();
  
  const [studentProfile, setStudentProfile] = useState(null);
  const [contextSummary, setContextSummary] = useState('');
  const [questionSet, setQuestionSet] = useState([]);
  const [dynamicQuestionGuidance, setDynamicQuestionGuidance] = useState('');
  const [synthesisInstructions, setSynthesisInstructions] = useState('');
  const [personalityProfile, setPersonalityProfile] = useState({
    motivationStyle: '',
    communicationPreference: '',
    resistancePatterns: '',
    energySource: '',
    decisionMaking: '',
    feedbackStyle: '',
    identityFramework: ''
  });
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedStudentUserId, setSelectedStudentUserId] = useState(null);
  const [claudeSynthesisInstructions, setClaudeSynthesisInstructions] = useState('');
  const [studentGradeLevel, setStudentGradeLevel] = useState('');
  
  // Initialize autosave hook
  const { 
    saveStatus, 
    lastSaved, 
    errorMessage, 
    save: autoSave, 
    saveNow,
    clearError 
  } = useFrameworkAutosave(selectedStudentUserId, 2000);
  
  // Section-specific instructions state
  const [sectionInstructions, setSectionInstructions] = useState({
    strategicAdvantage: '',
    intellectualSignature: '',
    hiddenSuperpowers: '',
    strategicPositioning: '',
    essayStrategy: '',
    collegeRecommendations: '',
    actionPlan: ''
  });
  
  // Student-specific considerations state
  const [studentSpecificConsiderations, setStudentSpecificConsiderations] = useState('');

  // Load student data
  useEffect(() => {
    const loadData = async () => {
      if (!student) return;
      
      try {
        setLoading(true);
        
        // Get the selected student user ID (this is the student's ID, not the logged-in parent)
        const savedStudentId = localStorage.getItem('college-selected-student-id');
        
        if (!savedStudentId) {
          console.error('No valid student user ID found. Please ensure a student is selected.');
          setLoading(false);
          return;
        }
        
        // savedStudentId should already be the student's user_id that we need
        // The parent selected this student, and we're customizing for them
        const studentUserId = savedStudentId;
        console.log('🔍 SynthesisInstructionsGenerator: Customizing for student:', studentUserId);
        console.log('📝 Note: Parent is customizing framework for their student');
        
        setSelectedStudentUserId(studentUserId);
        console.log('🔍 SynthesisInstructions: Loading data for student user_id:', studentUserId);
        
        // Load all phase data including student profile
        const { data: profileData, error } = await supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', studentUserId)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading student data:', error);
          return;
        }

        if (profileData) {
          console.log('✅ Loaded Phase 5 data:', profileData);
          
          // Set all the loaded data
          setStudentProfile(profileData);
          setContextSummary(profileData.strategic_context_summary || '');
          setQuestionSet(profileData.initial_question_set || []);
          setDynamicQuestionGuidance(profileData.dynamic_question_guidance || '');
          setSynthesisInstructions(profileData.synthesis_instructions || '');
          setClaudeSynthesisInstructions(profileData.claude_synthesis_instructions || '');
          setStudentGradeLevel(profileData.grade_level || '');
          
          // Load section-specific instructions if available
          if (profileData.section_synthesis_instructions) {
            setSectionInstructions(profileData.section_synthesis_instructions);
            console.log('✅ Loaded section synthesis instructions:', profileData.section_synthesis_instructions);
          }
          
          // Load student-specific considerations if available
          if (profileData.student_specific_considerations) {
            setStudentSpecificConsiderations(profileData.student_specific_considerations);
            console.log('✅ Loaded student-specific considerations:', profileData.student_specific_considerations);
          }
          
          // Load existing personality profile if available
          if (profileData.personality_profile) {
            try {
              const savedProfile = JSON.parse(profileData.personality_profile);
              setPersonalityProfile(prev => ({ ...prev, ...savedProfile }));
            } catch (error) {
              console.log('Error parsing personality profile:', error);
            }
          }
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [student?.id, familyUsers]);

  // Autosave synthesis instructions when they change using the hook
  useEffect(() => {
    if (!selectedStudentUserId || loading) return;
    
    // Only autosave if we have some content and it's been loaded
    const hasContent = Object.values(sectionInstructions).some(val => val.trim()) || 
                       studentSpecificConsiderations.trim() || 
                       claudeSynthesisInstructions.trim();
    
    if (hasContent) {
      autoSave({
        section_synthesis_instructions: sectionInstructions,
        student_specific_considerations: studentSpecificConsiderations,
        claude_synthesis_instructions: claudeSynthesisInstructions,
        synthesis_instructions: getSynthesisInstructions(),
        personality_profile: JSON.stringify(personalityProfile),
        assessment_phase: 5
      });
    }
  }, [sectionInstructions, studentSpecificConsiderations, claudeSynthesisInstructions, selectedStudentUserId, loading, autoSave, personalityProfile]);

  // Personality profile is now loaded from database (collected in Phase 1)


  const getDefaultSectionInstructions = () => {
    // Get timeline context for the student
    const studentType = getStudentType(studentGradeLevel);
    const timelineInfo = calculateTimelineMessage(studentGradeLevel);
    
    // Customize action timeline based on student type
    const actionTimeline = studentType === 'traditional' 
      ? 'Action Focus: Steps they can start immediately with clear timelines'
      : studentType === 'transfer'
      ? 'Action Focus: Transfer-specific steps aligned with both in-state public school deadlines and East Coastprivate school deadlines'
      : 'Action Focus: Foundation-building activities for the next 2+ years';
    
    return {
      strategicAdvantage: `Purpose: Help students see their unique value proposition clearly and confidently
Format: 2-3 sentences written directly to the student that capture what makes them genuinely different and valuable
Action Component: Include why this advantage matters for their specific goals and how to leverage it
Tone: Confident, specific, and affirming - should make them think "Yes, that's exactly who I am"`,
      
      intellectualSignature: `Purpose: Articulate their distinctive thinking style and learning approach
Components:
• Describe their unique problem-solving and learning patterns
• Connect thinking style to academic/career advantages
• Provide specific leverage points for applications
Action Component: End with concrete ways to showcase this intellectual approach in essays, interviews, and school selection
Strategic Output: One-sentence summary of their intellectual brand`,
      
      hiddenSuperpowers: `Purpose: Reframe unconventional traits as competitive advantages
Components:
• Identify 4-5 distinctive qualities they might undervalue
• Explain why each is actually a strength in college/career contexts
• Provide narrative framework for how these connect
Action Component: Specific ways to highlight these strengths in applications
Focus: Traits that feel "weird" or different but are actually valuable differentiators`,
      
      strategicPositioning: `Purpose: Define their personal brand and application strategy
Components:
• One-sentence personal brand statement
• Explanation of why authentic positioning beats "perfect" positioning
• Specific deployment strategies for different application components
Action Component: 3 specific schools that would especially value their profile
Strategic Output: Clear positioning that feels authentic and compelling`,
      
      essayStrategy: studentType === 'transfer'
        ? `Purpose: Identify compelling transfer narrative and academic growth story
Components:
• Why transfer now - the authentic reason for their educational journey
• Academic evolution story from CC to 4-year goals
• 3-4 specific transfer essay topics that show readiness
• Common transfer essay pitfalls to avoid
Action Component: Clear transfer rationale + evidence of preparation
Focus: Demonstrating readiness for upper-division work`
        : studentType === 'early_planner'
        ? `Purpose: Identify emerging themes and potential future essay topics
Components:
• Current interests that could develop into compelling stories
• Experiences they should document/pursue over next 2 years
• Types of activities that align with their authentic interests
Action Component: Essay topic development strategy for coming years
Focus: Building genuine experiences rather than resume padding`
        : `Purpose: Identify their most compelling stories and themes
Components:
• Their single best story that reveals character and potential
• 4 specific essay topics tailored to their experiences
• Common traps to avoid based on their profile
Action Component: Main theme admissions should remember + concrete essay approach
Focus: Stories that only they could tell, not generic topics`,
      
      collegeRecommendations: studentType === 'transfer' 
        ? `Purpose: Suggest transfer-friendly schools where they'd genuinely thrive
Components: 3 reach, 3 target, 3 safety schools with strong transfer programs
Action Component: Specific transfer pathways, articulation agreements, and why they'd thrive
Requirement: Focus on schools with robust transfer support and credit acceptance
Focus: Transfer success rates and community college partnerships`
        : studentType === 'early_planner'
        ? `Purpose: Suggest types of schools to explore over the next 2 years
Components: Categories of schools (e.g., "strong engineering programs", "liberal arts with research")
Action Component: What to look for when visiting/researching these school types
Requirement: Focus on fit characteristics rather than specific school names
Focus: Building criteria for future school selection`
        : `Purpose: Suggest schools where they'd genuinely thrive
Components: 3 reach, 3 target, 3 safety schools based on their actual interests and style
Action Component: Specific reasons why they'd thrive at each school
Requirement: Every recommendation must be profile-specific, not generic
Focus: Genuine fit over prestige or conventional metrics`,
      
      actionPlan: `Purpose: Immediate actions that create competitive momentum and prevent overwhelm
Components: 
• 🔴 This Week (Critical Path): 3 urgent actions with reasoning for immediate timing
• 🟡 This Month (Strategic Progress): Tasks with visible progress markers and outcomes
• 🟢 This Quarter (Long-term Positioning): Strategic moves that compound over time
${actionTimeline}
Action Focus: Steps they can execute within 24-48 hours with measurable impact
Customization: Align with their energy patterns and motivation style
Output: Concrete actions that build momentum rather than create overwhelm`,

      successMetrics: `Checkpoints to validate their competitive advantage deployment
Components: 
• Differentiation Test: Can they explain their unique value in 30 seconds?
• Competitive Advantage Validation: Are they positioned differently from similar applicants?
• Tactical Execution Tracker: Are they taking concrete weekly actions?
• Strategic Alignment Check: Do their targets match their advantages?`
    };
  };

  const getCustomizationPrompt = () => {
    const studentName = studentProfile?.name || student?.displayName || '[STUDENT NAME]';
    
    return `Please review and help me to refine the following guidance for API AI to generate an 8-section framework for a student insights report based on all collected data. The current draft is as follows:

**COMPREHENSIVE STRATEGIC INTELLIGENCE FRAMEWORK:**
This 8-section framework transforms student assessment data into immediately actionable competitive intelligence that reads like strategic consulting from someone who actually gets teenagers and knows the admissions game inside out. Rather than providing insights that need translation, this framework delivers tactical advantages students can deploy within 24-48 hours to dramatically outperform their competition.

Each section functions as insider intelligence: identifying their unique market position, providing tactical deployment strategies, delivering specific actions with measurable outcomes, and positioning them competitively against similar high-achieving applicants. The framework moves systematically from competitive analysis (what makes them different) to tactical advantage (how to use it) to operational execution (specific actions with deadlines).

**CRITICAL TEEN ENGAGEMENT PRINCIPLES:**
- **Cut Through Skepticism:** Lead with evidence from their responses, not generic advice
- **Respect Their Intelligence:** Acknowledge what they already know, then provide what they don't
- **Immediate Proof of Value:** Every insight includes something they can use within 24 hours
- **Authentic Voice:** Sound like a cool older sibling who got into great schools, not a consultant
- **Competitive Edge:** Show them advantages they didn't realize they had

**INSTRUCTIONS FOR THE EIGHT SECTIONS:**

## 1. YOUR STRATEGIC ADVANTAGE
**Purpose:** Immediate impact opening that establishes their competitive position with structured intelligence
**Format:** 400 words organized in distinct strategic subsections for maximum engagement
**Structure:**

### COMPETITIVE POSITIONING HEADLINE
One powerful sentence that captures their unique value proposition in the applicant pool using teen-optimized language.

### PRIMARY STRATEGIC ADVANTAGE  
The ONE thing that makes them genuinely unique among applicants. Include:
- Specific evidence from their responses (not generic claims)
- Why this matters to admissions committees (institutional value)
- How this differentiates from typical applicants with similar stats
- Teen-optimized framing: "Real talk: Here's what makes you different (and why it actually matters)"

### TACTICAL APPLICATIONS (Immediate Value)
For each insight, provide specific actions they can take within 24-48 hours:
- Essay Strategy: Which stories to tell and why (with specific angles)
- College Research: What to look for based on their profile (specific criteria)
- Interview Prep: Key messages to emphasize (with pivot strategies)

### COMPETITIVE INTELLIGENCE
- What similar applicants typically emphasize (so they can differentiate)
- Potential vulnerabilities in their profile and how to address them strategically
- Strategic positioning vs their likely competition (with specific tactics)

**Success Metric:** They should think "Finally, someone gets what makes me different AND knows how to use it"

## 2. HOW YOUR BRAIN ACTUALLY WORKS
**Purpose:** Validate their thinking style as a competitive weapon
**Teen-Optimized Opening:** "You think differently than most people. Here's why that's actually your superpower."
**Format:** 350 words with specific examples from their assessment responses
**Components:**
• Signature thinking patterns that create advantages (with proof)
• Intellectual superpowers that set them apart from typical applicants
• Learning architecture that colleges value but most students can't articulate
• Creative problem-solving approach with tactical applications
**Action Component:** Concrete ways to showcase this intellectual approach in essays, interviews, and school selection
**Evidence Requirement:** Must use specific examples from their responses, not generic descriptions

## 3. YOUR ACTUAL SECRET WEAPONS
**Purpose:** Transform things they undervalue into recognized competitive advantages
**Teen-Optimized Opening:** "These are things you do that other applicants wish they could do."
**Format:** 400 words identifying 4-5 distinctive qualities with deployment strategies
**Components:**
• Weapon #1-4: [Specific strength] + How to deploy it + Where it wins
• Competitive context showing what others miss that they should emphasize
• Tactical deployment strategies for each advantage across applications
• Reality check explaining why these aren't "weird" but valuable differentiators
**Action Component:** Specific ways to highlight these strengths in applications
**Focus:** Transform ordinary traits into extraordinary competitive advantages

## 4. HOW YOU ACTUALLY STAND OUT
**Purpose:** Define their authentic brand position vs. typical "well-rounded" applicants
**Teen-Optimized Opening:** "While everyone else is trying to be 'well-rounded,' here's your real angle."
**Format:** 300 words with clear positioning strategy
**Components:**
• One-sentence personal brand statement that captures their unique value
• Strategic authenticity explanation (why being genuine beats gaming the system)
• Deployment strategies for different application components
• Competitive differentiation from similar applicants
**Action Component:** 3 specific schools where this positioning creates maximum advantage
**Authenticity Check:** "This isn't about being fake - it's about being smart with what's already true"

## 5. YOUR ESSAY STRATEGY THAT ACTUALLY WORKS
**Purpose:** Comprehensive tactical guidance for application domination
**Teen-Optimized Opening:** "Forget the generic 'overcoming challenges' essay. Here's your actual story."
**Format:** 500 words with immediate tactical value
**Components:**
• Personal Statement Strategy: Specific opening angle + narrative arc + differentiation tactics
• Supplemental Essay Tactics: School-specific approaches and unique angles
• Interview Domination: Key messages + signature stories + power moves
• Strategic trap avoidance based on their specific profile
**Action Component:** Specific writing prompts and opening lines they can use immediately
**Focus:** Stories only they could tell, not generic topics everyone uses

## 6. SCHOOLS WHERE YOU'D ACTUALLY BE HAPPY (AND THEY'D WANT YOU)
**Purpose:** Strategic college targeting based on genuine fit, competitive advantage, and realistic probability assessment
**Teen-Optimized Framing:** Move beyond prestige obsession to strategic advantage and success probability
**Format:** 400 words with specific school recommendations and probability analysis
**Components:**
• **Reach Schools - "reach Big (But Smart)"**: Strategic fit analysis + probability assessment + unique positioning angle + why they're competitive despite selectivity
• **Target Schools - "Your Sweet Spot"**: High probability match + strategic fit + specific advantages they bring + connection points to emphasize
• **Safety Schools - "Smart Choices That Come with Money"**: High probability + strategic value + scholarship potential + why this enhances rather than limits their options
**Critical Requirements:**
- **Every school recommendation** must emphasize strategic fit AND probability of success
- Include specific programs, professors, opportunities, and institutional needs they fulfill
- Explain their competitive positioning at each school level
- Address merit aid potential and strategic value beyond just admission probability
**Action Component:** Specific positioning strategies and tactical approaches for each school with probability-based application strategy
**Reality Check:** Focus on genuine fit where their profile creates competitive advantage AND realistic success probability

## 7. DO THIS FIRST (BEFORE YOU FORGET)
**Purpose:** Immediate actions that create competitive momentum and prevent overwhelm
**Teen-Optimized Structure:** Prioritized by urgency with clear reasoning
**Format:** 300 words with time-sensitive tactical guidance
**Components:**
• 🔴 This Week (Critical Path): 3 urgent actions with reasoning for immediate timing
• 🟡 This Month (Strategic Progress): Tasks with visible progress markers and outcomes
• 🟢 This Quarter (Long-term Positioning): Strategic moves that compound over time
**Action Focus:** Steps they can execute within 24-48 hours with measurable impact
**Motivation Design:** Actions that build momentum rather than create overwhelm

## 8. SUCCESS METRICS - KNOW YOU'RE WINNING
**Purpose:** Checkpoints to validate their competitive advantage deployment
**Format:** 200 words with clear success indicators
**Components:**
• Differentiation Test: Can they explain their unique value in 30 seconds?
• Competitive Advantage Validation: Are they positioned differently from similar applicants?
• Tactical Execution Tracker: Are they taking concrete weekly actions?
• Strategic Alignment Check: Do their targets match their advantages?
**Success Validation:** Clear benchmarks for competitive positioning effectiveness

**ADDITIONAL GUIDANCE FOR ALL SECTIONS:**

**TEEN ENGAGEMENT REQUIREMENTS:**
- Write everything in SECOND PERSON ("You" not "He/She/They")
- Use language that cuts through admissions advice fatigue - be direct, evidence-based, and genuine
- Avoid corporate speak - use words a smart 17-year-old would actually use and trust
- Lead with specific proof from their responses, not generic claims
- Include immediate tactical applications for every major insight

**COMPETITIVE INTELLIGENCE FOCUS:**
- Position insights as strategic advantages, not just personality traits
- Include competitive context showing how they differentiate from similar applicants
- Provide tactical deployment strategies with specific timing (24-48 hours)
- Focus on market positioning and authentic competitive advantage

**EVIDENCE-BASED APPROACH:**
- Every insight must be backed by specific examples from their assessment responses
- Use "When you [specific example], that showed..." format to prove points
- Avoid generic advice that could apply to any student
- Connect insights directly to their demonstrated patterns and behaviors

**SUCCESS METRICS:**
- Do they say "This is exactly what I needed to hear"?
- Do they feel they have insider knowledge and unfair advantages?
- Are the tactical actions clear and immediately executable?
- Do they see their authentic self as a competitive weapon?
- Would they screenshot sections to reference later and show friends?

This guidance should be comprehensive enough that API AI can generate a highly personalized, actionable insights report that transforms [name]'s assessment data into an immediately implementable strategic plan for college admissions success.

Please provide customized instructions for each section and any additional guidance modifications that will help generate synthesis instructions specifically tailored to [name]'s unique profile, interests, and characteristics.`;
  };

  const getSynthesisInstructions = () => {
    const studentName = studentProfile?.name || student?.displayName || '[student name]';
    
    // Get timeline context
    const studentType = getStudentType(studentGradeLevel);
    const timelineInfo = calculateTimelineMessage(studentGradeLevel);
    
    // Get timeline-aware defaults first
    const timelineAwareDefaults = getDefaultSectionInstructions();
    
    // Merge with any saved customizations, prioritizing saved content but using timeline-aware defaults as fallback
    const sections = {
      strategicAdvantage: sectionInstructions?.strategicAdvantage || timelineAwareDefaults.strategicAdvantage,
      intellectualSignature: sectionInstructions?.intellectualSignature || timelineAwareDefaults.intellectualSignature,
      hiddenSuperpowers: sectionInstructions?.hiddenSuperpowers || timelineAwareDefaults.hiddenSuperpowers,
      strategicPositioning: sectionInstructions?.strategicPositioning || timelineAwareDefaults.strategicPositioning,
      essayStrategy: sectionInstructions?.essayStrategy || timelineAwareDefaults.essayStrategy,
      collegeRecommendations: sectionInstructions?.collegeRecommendations || timelineAwareDefaults.collegeRecommendations,
      actionPlan: sectionInstructions?.actionPlan || timelineAwareDefaults.actionPlan,
      successMetrics: sectionInstructions?.successMetrics || timelineAwareDefaults.successMetrics
    };

    // Add timeline context intro based on student type
    const timelineContext = studentType === 'traditional'
      ? `**TIMELINE CONTEXT:** ${timelineInfo.primary}. This assessment focuses on immediate application strategy and execution.`
      : studentType === 'transfer'
      ? `**TIMELINE CONTEXT:** ${timelineInfo.primary}. This assessment emphasizes transfer readiness and articulation of academic journey.`
      : `**TIMELINE CONTEXT:** ${timelineInfo.primary}. This assessment focuses on foundation-building and long-term strategy.`;

    return `${timelineContext}

**FRAMEWORK INTRODUCTION:**
The following 8-section framework is designed to generate a comprehensive, action-oriented insights report that transforms student assessment data into an immediately implementable strategic plan. Rather than providing generic college admissions guidance, this framework creates a personalized roadmap that helps ${studentName} recognize their authentic competitive advantages and take concrete steps toward successful applications.

Each section serves a dual purpose: providing strategic insight about the student's unique value while delivering specific, actionable next steps they can implement immediately. The framework moves systematically from self-awareness (recognizing their distinctive strengths) to strategic positioning (leveraging those strengths effectively) to tactical execution (concrete actions with clear timelines).

This approach ensures ${studentName} finishes with both confidence in their authentic value proposition and crystal-clear direction for their application process. The report should feel like receiving insider intelligence and a personalized game plan, not a counseling session summary requiring further interpretation.

**INSTRUCTIONS FOR THE EIGHT SECTIONS:**

## 1. YOUR STRATEGIC ADVANTAGE
${sections.strategicAdvantage || getDefaultSectionInstructions().strategicAdvantage}

## 2. HOW YOUR BRAIN WORKS (Intellectual Signature)
${sections.intellectualSignature || getDefaultSectionInstructions().intellectualSignature}

## 3. YOUR SECRET WEAPONS (Hidden Superpowers)  
${sections.hiddenSuperpowers || getDefaultSectionInstructions().hiddenSuperpowers}

## 4. HOW TO STAND OUT (Strategic Positioning)
${sections.strategicPositioning || getDefaultSectionInstructions().strategicPositioning}

## 5. ESSAY GAME PLAN
${sections.essayStrategy || getDefaultSectionInstructions().essayStrategy}

## 6. COLLEGE RECOMMENDATIONS
${sections.collegeRecommendations || getDefaultSectionInstructions().collegeRecommendations}

## 7. YOUR NEXT STEPS (Action Plan)
${sections.actionPlan || getDefaultSectionInstructions().actionPlan}

## 8. SUCCESS METRICS
${sections.successMetrics || getDefaultSectionInstructions().successMetrics}

**STUDENT-SPECIFIC CONSIDERATIONS:**
${studentSpecificConsiderations || '(No specific considerations added)'}

**ADDITIONAL GUIDANCE:**

**TEEN ENGAGEMENT REQUIREMENTS:**
- Write everything in SECOND PERSON ("You" not "He/She/They")
- Use language that cuts through admissions advice fatigue - be direct, evidence-based, and genuine
- Avoid corporate speak - use words a smart 17-year-old would actually use and trust
- Lead with specific proof from their responses, not generic claims
- Include immediate tactical applications for every major insight

**COMPETITIVE INTELLIGENCE FOCUS:**
- Position insights as strategic advantages, not just personality traits
- Include competitive context showing how they differentiate from similar applicants
- Provide tactical deployment strategies with specific timing (24-48 hours)
- Focus on market positioning and authentic competitive advantage

**EVIDENCE-BASED APPROACH:**
- Every insight must be backed by specific examples from their assessment responses
- Use "When you [specific example], that showed..." format to prove points
- Avoid generic advice that could apply to any student
- Connect insights directly to their demonstrated patterns and behaviors

**SUCCESS METRICS:**
- Do they say "This is exactly what I needed to hear"?
- Do they feel they have insider knowledge and unfair advantages?
- Are the tactical actions clear and immediately executable?
- Do they see their authentic self as a competitive weapon?
- Would they screenshot sections to reference later and show friends?

This guidance should be comprehensive enough that API AI can generate a highly personalized, actionable insights report that transforms ${studentName}'s assessment data into an immediately implementable strategic plan for college admissions success.

The final report should feel like personalized advice from someone who truly understands ${studentName}, using language and examples that authentically connect with their specific motivations, communication style, and identity.`;
  };

  const copyInstructionsToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getSynthesisInstructions());
      setCopySuccess('copied');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('failed');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  // Personality profile is saved in Phase 1, no longer editable here

  const saveSynthesisInstructions = async () => {
    if (!selectedStudentUserId) {
      console.error('❌ CRITICAL: No selectedStudentUserId for saving synthesis instructions');
      return false;
    }
    
    console.log('💾 SAVING SYNTHESIS INSTRUCTIONS:', {
      selectedStudentUserId,
      synthesisInstructionsLength: getSynthesisInstructions().length,
      personalityProfile,
      sectionInstructions,
      studentSpecificConsiderations,
      instructionsPreview: getSynthesisInstructions().substring(0, 200)
    });
    
    setSaving(true);
    try {
      // Prepare the data to save
      const updateData = {
        synthesis_instructions: getSynthesisInstructions(),
        claude_synthesis_instructions: claudeSynthesisInstructions,
        section_synthesis_instructions: sectionInstructions,
        student_specific_considerations: studentSpecificConsiderations,
        personality_profile: JSON.stringify(personalityProfile),
        assessment_phase: 5
      };
      
      // Use the hook's saveNow method for immediate save
      const success = await saveNow(updateData);
      
      if (success) {
        // Update master strategy
        await updateMasterStrategy({
          synthesis_instructions: getSynthesisInstructions(),
          section_synthesis_instructions: sectionInstructions,
          student_specific_considerations: studentSpecificConsiderations,
          phase_5_completed: true,
          updated_at: new Date().toISOString()
        });
        
        console.log('✅ Synthesis instructions saved successfully');
        return true;
      } else {
        throw new Error('Save operation failed');
      }
      
    } catch (error) {
      console.error('Error saving synthesis instructions:', error);
      alert('Failed to save synthesis instructions. Please try again. Error: ' + error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading Student Data</h2>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Student Found</h3>
          <p className="text-slate-600 mb-4">Please select a student to generate synthesis instructions.</p>
          <button
            onClick={() => navigate('/college')}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
          >
            Return to College Hub
          </button>
        </div>
      </div>
    );
  }

  // Check if personality profile was completed in Phase 1
  const hasPersonalityProfile = personalityProfile && Object.values(personalityProfile).filter(v => v).length > 0;
  console.log('🔍 SynthesisInstructionsGenerator: Personality profile status:', { hasPersonalityProfile, personalityProfile });

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/college/dynamic-guidance-generator')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dynamic Guidance
        </button>
        
        <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full border border-slate-700">
              <Brain className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white">Phase 5: Synthesis Instructions</span>
            </div>
            <SaveStatusIndicator status={saveStatus} errorMessage={errorMessage} lastSaved={lastSaved} />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Synthesis Instructions Generator</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Create personalized AI instructions for <strong>{student.displayName || student.name}</strong>'s strategic report using the personality profile from Phase 1.
          </p>
        </div>
      </div>

      {/* Personality Profile Status */}
      {hasPersonalityProfile ? (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 mb-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Personality Profile Loaded
          </h2>
          
          <p className="text-sm text-slate-600 mb-4">
            Using personality profile completed in Phase 1 for {student.displayName || student.name}.
          </p>
          
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {personalityProfile.motivationStyle && (
                <div>
                  <span className="font-medium text-slate-700">Motivation:</span>
                  <span className="text-slate-600 ml-2">{personalityProfile.motivationStyle}</span>
                </div>
              )}
              {personalityProfile.communicationPreference && (
                <div>
                  <span className="font-medium text-slate-700">Communication:</span>
                  <span className="text-slate-600 ml-2">{personalityProfile.communicationPreference}</span>
                </div>
              )}
              {personalityProfile.energySource && (
                <div>
                  <span className="font-medium text-slate-700">Energy Source:</span>
                  <span className="text-slate-600 ml-2">{personalityProfile.energySource}</span>
                </div>
              )}
              {personalityProfile.decisionMaking && (
                <div>
                  <span className="font-medium text-slate-700">Decision Making:</span>
                  <span className="text-slate-600 ml-2">{personalityProfile.decisionMaking}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 mb-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Personality Profile Missing
          </h2>
          
          <p className="text-sm text-slate-600 mb-4">
            No personality profile found from Phase 1. Please complete the personality assessment in the Comprehensive Student Profile.
          </p>
          
          <button
            onClick={() => navigate('/college/academic-profile')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Complete Personality Profile
          </button>
        </div>
      )}

      {/* Customize with Claude Helper */}
      {hasPersonalityProfile && (
        <div className="bg-gradient-to-br from-azure-50 to-indigo-50 border-2 border-azure-200 rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-azure-600" />
            Step 1: Customize with Claude
          </h2>
          
          <p className="text-sm text-slate-700 mb-4">
            Copy the prompt below to work with Claude on customizing the synthesis instructions specifically for {student.displayName || student.name}. Claude will help you tailor each section to their unique profile.
          </p>
          
          <div className="bg-white border border-azure-200 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-slate-600">Customization Prompt for Claude</span>
              <button
                onClick={() => {
                  const prompt = getCustomizationPrompt();
                  navigator.clipboard.writeText(prompt);
                  setCopySuccess('prompt-copied');
                  setTimeout(() => setCopySuccess(''), 2000);
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-azure-100 hover:bg-azure-200 rounded"
              >
                <Copy className="w-3 h-3" />
                {copySuccess === 'prompt-copied' ? 'Copied!' : 'Copy Prompt'}
              </button>
            </div>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
              {getCustomizationPrompt()}
            </pre>
          </div>
          
          <div className="bg-azure-100 border border-azure-300 rounded-lg p-3">
            <p className="text-sm text-azure-800">
              <strong>Workflow:</strong> Copy the prompt above → Paste into Claude → Get customized instructions → 
              Paste Claude's response into the sections below → Review complete instructions
            </p>
          </div>
        </div>
      )}

      {/* Section-Based Instructions */}
      {hasPersonalityProfile && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              Step 2: Customize Synthesis Instructions by Section
            </h2>
            
            <p className="text-sm text-slate-700 mb-6">
              After working with Claude, paste the customized instructions for each section below. 
              These will be combined into complete synthesis instructions for {student.displayName || student.name}'s report.
            </p>
            
            {/* Section Instructions */}
            <div className="space-y-6">
              {/* Strategic Advantage */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Section 1: Your Strategic Advantage
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  A 2-3 sentence summary that captures what makes them awesome and different.
                </p>
                <textarea
                  value={sectionInstructions.strategicAdvantage}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, strategicAdvantage: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().strategicAdvantage}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>

              {/* Intellectual Signature */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  Section 2: How Your Brain Works (Intellectual Signature)
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  What makes their thinking style unique and how to leverage it.
                </p>
                <textarea
                  value={sectionInstructions.intellectualSignature}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, intellectualSignature: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().intellectualSignature}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>

              {/* Hidden Superpowers */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Section 3: Your Secret Weapons (Hidden Superpowers)
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  4-5 things they do differently that are actually competitive advantages.
                </p>
                <textarea
                  value={sectionInstructions.hiddenSuperpowers}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, hiddenSuperpowers: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().hiddenSuperpowers}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>

              {/* Strategic Positioning */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Section 4: How to Stand Out (Strategic Positioning)
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Their personal brand and how to deploy it across applications.
                </p>
                <textarea
                  value={sectionInstructions.strategicPositioning}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, strategicPositioning: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().strategicPositioning}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>

              {/* Essay Strategy */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Section 5: Essay Game Plan
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Their best stories and specific essay topics that will make them memorable.
                </p>
                <textarea
                  value={sectionInstructions.essayStrategy}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, essayStrategy: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().essayStrategy}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>

              {/* College Recommendations */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Compass className="w-4 h-4" />
                  Section 6: College Recommendations
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Specific schools where they would genuinely thrive, organized by reach/target/safety.
                </p>
                <textarea
                  value={sectionInstructions.collegeRecommendations}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, collegeRecommendations: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().collegeRecommendations}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>

              {/* Action Plan */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Section 7: Your Next Steps (Action Plan)
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  6 specific, time-bound actions aligned with their energy and motivation patterns.
                </p>
                <textarea
                  value={sectionInstructions.actionPlan}
                  onChange={(e) => setSectionInstructions(prev => ({ ...prev, actionPlan: e.target.value }))}
                  placeholder={getDefaultSectionInstructions().actionPlan}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
                  rows="3"
                />
              </div>
           

          {/* Success Metrics */}
          <div className="border border-slate-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Section 8: Success Metrics
            </h3>
            <p className="text-sm text-slate-600 mb-3">
              Checkpoints to validate their competitive advantage deployment.
            </p>
            <textarea
              value={sectionInstructions.successMetrics}
              onChange={(e) => setSectionInstructions(prev => ({ ...prev, successMetrics: e.target.value }))}
              placeholder={getDefaultSectionInstructions().successMetrics}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
              rows="3"
            />
          </div>
          
          </div>
          </div>

          {/* Student-Specific Considerations */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Student-Specific Considerations (Optional)
            </h2>
            
            <p className="text-sm text-slate-700 mb-4">
              Add any student-specific considerations that should guide the AI's analysis, such as values integration, identity affirmation, capability recognition, or relationship focus. This helps ensure the synthesis instructions are deeply personalized for {student.displayName || student.name}.
            </p>
            
            <textarea
              value={studentSpecificConsiderations}
              onChange={(e) => setStudentSpecificConsiderations(e.target.value)}
              placeholder="Example: Values Integration - Frame everything around authentic development, genuine contribution, and finding communities where they belong rather than trying to fit conventional molds. Identity Affirmation - Position their unique identity and experiences as valuable perspectives rather than obstacles to overcome..."
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none text-sm"
              rows="4"
            />
          </div>

          {/* Copy Full Instructions */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Step 3: Complete Synthesis Instructions
            </h2>
            
            <p className="text-sm text-slate-700 mb-4">
              These instructions auto-combine your customized sections above. Copy them for a final review/polish with Claude, then paste the final version below.
            </p>
            
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-slate-600">Full Instructions with Your Customizations</span>
                <button
                  onClick={copyInstructionsToClipboard}
                  className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                >
                  <Copy className="w-3 h-3" />
                  {copySuccess === 'copied' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap max-h-96 overflow-y-auto">
                {getSynthesisInstructions()}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Claude Synthesis Instructions Input */}
      {hasPersonalityProfile && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Step 4: Final Synthesis Instructions
          </h2>
          
          <p className="text-sm text-slate-700 mb-4">
            After reviewing/polishing the complete instructions with Claude, paste the final synthesis instructions here:
          </p>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Final Synthesis Instructions
            </label>
            <textarea
              value={claudeSynthesisInstructions}
              onChange={(e) => setClaudeSynthesisInstructions(e.target.value)}
              placeholder="Paste the final, polished synthesis instructions from Claude here..."
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
              rows="8"
            />
          </div>
          
          {claudeSynthesisInstructions.trim() && (
            <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-700">
                ✅ Synthesis instructions captured! You can now proceed to framework customization.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={() => navigate('/college/dynamic-guidance-generator')}
          className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dynamic Guidance
        </button>

        <div className="flex items-center gap-3">
          {hasPersonalityProfile && (
            <button
              onClick={async () => {
                const success = await saveSynthesisInstructions();
                if (success) {
                  // Show success message
                  alert('Synthesis instructions saved successfully!');
                  navigate('/college/dynamic-guidance-generator');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              disabled={saving}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save and Close'}
            </button>
          )}
          
          {hasPersonalityProfile && claudeSynthesisInstructions.trim() && (
            <button
              onClick={async () => {
                const success = await saveSynthesisInstructions();
                if (success) {
                  navigate('/college/formatting-customizer');
                }
              }}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
              disabled={saving}
            >
              <ArrowRight className="w-4 h-4" />
              {saving ? 'Saving...' : 'Continue to Format Customization'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SynthesisInstructionsGenerator;